import { DragOverlay as DndKitDragOverlay } from '@dnd-kit/core';
import type { CSSProperties, ReactNode } from 'react';

interface DragOverlayProps {
  /**
   * The content to render in the overlay
   * @type {ReactNode}
   * @required false
   * @default undefined
   */
  children?: ReactNode;
  /**
   * Optional CSS class to apply to the overlay
   * @type {string}
   * @required false
   * @default undefined
   */
  className?: string;
  /**
   * Optional inline styles to apply to the overlay
   * @type {CSSProperties}
   * @required false
   * @default undefined
   */
  style?: CSSProperties;
  /**
   * Whether the overlay is currently active/visible
   * @type {boolean}
   * @required false
   * @default true
   */
  isActive?: boolean;
  /**
   * Adjustment to overlay position along the x-axis
   * @type {number}
   * @required false
   * @default 0
   */
  adjustX?: number;
  /**
   * Adjustment to overlay position along the y-axis
   * @type {number}
   * @required false
   * @default 0
   */
  adjustY?: number;
  /**
   * Animation configuration for when the dragged item is dropped
   * Set to null to disable drop animation
   * @type {{duration: number, easing: string} | null}
   * @required false
   * @default {duration: 200, easing: 'cubic-bezier(0.18, 0.67, 0.6, 1.22)'}
   */
  dropAnimation?: {
    duration: number;
    easing: string;
  } | null;
}

/**
 * A component that renders a floating overlay for the currently dragged item
 */
const DragOverlay = ({
  children,
  className,
  style,
  adjustX = 0,
  adjustY = 0,
  isActive = true,
  dropAnimation = {
    duration: 200,
    easing: 'cubic-bezier(0.18, 0.67, 0.6, 1.22)',
  },
}: DragOverlayProps) => {
  const overlayStyle: CSSProperties = {
    ...style,
    cursor: 'grabbing',
    zIndex: 1000,
    transformOrigin: 'center',
    pointerEvents: 'none',
  };

  return (
    <DndKitDragOverlay
      className={className}
      style={overlayStyle}
      dropAnimation={dropAnimation}
      modifiers={[
        ({ transform }) => ({
          ...transform,
          x: transform.x + adjustX,
          y: transform.y + adjustY,
        }),
      ]}
    >
      {isActive && children}
    </DndKitDragOverlay>
  );
};

export { DragOverlay };
export type { DragOverlayProps };
